import { useEffect, useState } from 'react';
import { cameraOf, noteSwitch, TILTED_DEGREES } from '../globe/viewSync';
import { useViewMode, type ViewMode } from '../../state/viewMode';

const MODES: readonly { id: ViewMode; label: string; title: string }[] = [
  { id: 'map', label: 'MAP', title: '2D map' },
  { id: 'bim', label: 'BIM', title: 'The engineering model' },
  { id: 'globe', label: 'GLOBE', title: '3D scene' },
];

const NOTICE_MS = 2400;

/**
 * Map · BIM · Globe: three views of the same place. A switch hands the camera of the view being left to the view
 * being entered (BIM hands over its own). Map has no tilt, so leaving a tilted Globe lands flat there, and the
 * switch says so rather than flattening in silence; Globe keeps its tilt for the way back.
 */
export function ModeSwitch() {
  const mode = useViewMode((s) => s.mode);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(t);
  }, [notice]);

  const go = (next: ViewMode): void => {
    const view = useViewMode.getState();
    if (next === view.mode) return;
    const camera = cameraOf(view.mode);
    if (camera) view.handOver(camera);
    noteSwitch(view.mode, camera);
    setNotice(
      view.mode === 'globe' && next === 'map' && camera && camera.pitch > TILTED_DEGREES
        ? 'Map is flat. Globe keeps the tilt.'
        : null,
    );
    view.setMode(next);
  };

  return (
    <>
      <div className="mm-mode-seg" role="group" aria-label="View">
        {MODES.map((m) => (
          <button key={m.id} type="button" aria-pressed={m.id === mode} title={m.title} onClick={() => go(m.id)}>
            {m.label}
          </button>
        ))}
      </div>
      {notice && (
        <div className="mm-mode-note" role="status">
          {notice}
        </div>
      )}
    </>
  );
}
